import type { Receipt } from "@sobidogam/storage";

export * from "./ics";
export * from "./import";

export type ReportPeriod = "week" | "month" | "year";

export interface ReportSummary {
	period: string;
	unlockedCount: number;
	totalCells: number;
	streakDays: number;
	topCategoryIds: string[];
	percentile?: number;
	recordCount?: number;
	totalAmount?: number;
	categoryCounts?: Record<string, number>;
	/** Amount per category in the window (#37 분류 비율 그래프). */
	categoryAmounts?: Record<string, number>;
}

export interface ReportComparison {
	period: ReportPeriod;
	current: ReportSummary;
	previous: ReportSummary;
	delta: {
		recordCount: number;
		unlockedCount: number;
		totalAmount: number;
	};
}

export interface MemoryArchiveSummary {
	year: number;
	recordCount: number;
	enrichedCount: number;
	favoriteCount: number;
	revisitedPlaceCount: number;
	mostActiveSeason: string | null;
	mostActiveWeekday: string | null;
	topRevisitedPlaces: Array<{ storeDisplay: string; visits: number }>;
	moments: Array<{
		receiptId: string;
		date: string;
		storeDisplay: string;
		memo: string;
		mood: Receipt["mood"];
	}>;
}

const SEASON_LABELS = ["겨울", "봄", "여름", "가을"] as const;
const WEEKDAY_LABELS = [
	"일요일",
	"월요일",
	"화요일",
	"수요일",
	"목요일",
	"금요일",
	"토요일",
] as const;

function kstDateParts(value: string) {
	const date = new Date(value);
	if (!Number.isFinite(date.getTime())) return null;
	const parts = new Intl.DateTimeFormat("en-CA", {
		timeZone: "Asia/Seoul",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).formatToParts(date);
	const number = (type: Intl.DateTimeFormatPartTypes) =>
		Number(parts.find((part) => part.type === type)?.value);
	const year = number("year");
	const month = number("month");
	const day = number("day");
	if (!year || !month || !day) return null;
	return {
		year,
		month,
		day,
		date: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
		weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
	};
}

/** Builds the paid annual archive only from local, non-deleted records. */
export function buildMemoryArchive(
	receipts: Receipt[],
	year: number,
): MemoryArchiveSummary {
	const records = receipts
		.map((receipt) => ({ receipt, parts: kstDateParts(receipt.transactedAt) }))
		.filter(
			(
				item,
			): item is { receipt: Receipt; parts: NonNullable<typeof item.parts> } =>
				!item.receipt.deletedAt &&
				item.parts !== null &&
				item.parts.year === year,
		);
	const seasonCounts = new Map<string, number>();
	const weekdayCounts = new Map<string, number>();
	const stores = new Map<string, { storeDisplay: string; visits: number }>();
	for (const { receipt, parts } of records) {
		const seasonIndex = Math.floor((parts.month % 12) / 3);
		const season = SEASON_LABELS[seasonIndex] ?? "겨울";
		seasonCounts.set(season, (seasonCounts.get(season) ?? 0) + 1);
		const weekday = WEEKDAY_LABELS[parts.weekday] ?? "일요일";
		weekdayCounts.set(weekday, (weekdayCounts.get(weekday) ?? 0) + 1);
		const storeKey =
			receipt.storeKey ||
			receipt.storeDisplay.trim().toLocaleLowerCase("ko-KR");
		const current = stores.get(storeKey);
		stores.set(storeKey, {
			storeDisplay: current?.storeDisplay ?? receipt.storeDisplay,
			visits: (current?.visits ?? 0) + 1,
		});
	}
	const topLabel = (counts: Map<string, number>) =>
		[...counts.entries()].sort(
			(a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "ko"),
		)[0]?.[0] ?? null;
	const topRevisitedPlaces = [...stores.values()]
		.filter((store) => store.visits > 1)
		.sort(
			(a, b) =>
				b.visits - a.visits ||
				a.storeDisplay.localeCompare(b.storeDisplay, "ko"),
		)
		.slice(0, 5);
	const moments = records
		.filter(
			({ receipt }) =>
				!receipt.sensitiveMasked && Boolean(receipt.memo?.trim()),
		)
		.sort((a, b) => {
			const favoriteDelta =
				Number(Boolean(b.receipt.isFavorite)) -
				Number(Boolean(a.receipt.isFavorite));
			return (
				favoriteDelta ||
				Date.parse(b.receipt.transactedAt) - Date.parse(a.receipt.transactedAt)
			);
		})
		.slice(0, 12)
		.map(({ receipt, parts }) => ({
			receiptId: receipt.receiptId,
			date: parts.date,
			storeDisplay: receipt.storeDisplay,
			memo: receipt.memo?.trim() ?? "",
			mood: receipt.mood ?? null,
		}));
	return {
		year,
		recordCount: records.length,
		enrichedCount: records.filter(({ receipt }) =>
			Boolean(
				receipt.memo || receipt.mood || receipt.occasion || receipt.isFavorite,
			),
		).length,
		favoriteCount: records.filter(({ receipt }) => receipt.isFavorite).length,
		revisitedPlaceCount: topRevisitedPlaces.length,
		mostActiveSeason: topLabel(seasonCounts),
		mostActiveWeekday: topLabel(weekdayCounts),
		topRevisitedPlaces,
		moments,
	};
}

interface ReportWindow {
	key: string;
	start: Date;
	end: Date;
}

const DAY_MS = 86_400_000;

function utcDay(date: Date): Date {
	return new Date(
		Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
	);
}

function addDays(date: Date, count: number): Date {
	return new Date(date.getTime() + count * DAY_MS);
}

function addMonths(date: Date, count: number, day: number): Date {
	const next = new Date(
		Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + count, 1),
	);
	return new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth(), day));
}

function customMonthStart(anchor: Date, monthStartDay: number): Date {
	const day = Math.min(28, Math.max(1, Math.trunc(monthStartDay)));
	const monthStart = new Date(
		Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), day),
	);
	return anchor.getUTCDate() >= day
		? monthStart
		: addMonths(monthStart, -1, day);
}

function windowFor(
	period: ReportPeriod,
	anchor: Date,
	monthStartDay: number,
): {
	current: ReportWindow;
	previous: ReportWindow;
} {
	const day = utcDay(anchor);
	if (period === "week") {
		const end = addDays(day, 1);
		const start = addDays(end, -7);
		const previousStart = addDays(start, -7);
		return {
			current: { key: start.toISOString().slice(0, 10), start, end },
			previous: {
				key: previousStart.toISOString().slice(0, 10),
				start: previousStart,
				end: start,
			},
		};
	}
	if (period === "month") {
		const start = customMonthStart(day, monthStartDay);
		const end = addMonths(start, 1, Math.min(28, Math.max(1, monthStartDay)));
		const previousStart = addMonths(
			start,
			-1,
			Math.min(28, Math.max(1, monthStartDay)),
		);
		return {
			current: { key: start.toISOString().slice(0, 10), start, end },
			previous: {
				key: previousStart.toISOString().slice(0, 10),
				start: previousStart,
				end: start,
			},
		};
	}
	const start = new Date(Date.UTC(day.getUTCFullYear(), 0, 1));
	const end = new Date(Date.UTC(day.getUTCFullYear() + 1, 0, 1));
	const previousStart = new Date(Date.UTC(day.getUTCFullYear() - 1, 0, 1));
	return {
		current: { key: String(day.getUTCFullYear()), start, end },
		previous: {
			key: String(day.getUTCFullYear() - 1),
			start: previousStart,
			end: start,
		},
	};
}

function recordsIn(receipts: Receipt[], window: ReportWindow): Receipt[] {
	return receipts.filter((receipt) => {
		if (receipt.deletedAt) return false;
		const time = Date.parse(receipt.transactedAt);
		return (
			Number.isFinite(time) &&
			time >= window.start.getTime() &&
			time < window.end.getTime()
		);
	});
}

function longestStreak(receipts: Receipt[]): number {
	const days = [
		...new Set(
			receipts.map((receipt) =>
				utcDay(new Date(receipt.transactedAt)).getTime(),
			),
		),
	].sort((a, b) => a - b);
	let best = 0;
	let current = 0;
	let previous: number | undefined;
	for (const time of days) {
		current =
			previous !== undefined && time - previous === DAY_MS ? current + 1 : 1;
		best = Math.max(best, current);
		previous = time;
	}
	return best;
}

function summarizeWindow(
	receipts: Receipt[],
	window: ReportWindow,
): ReportSummary {
	const records = recordsIn(receipts, window);
	const categoryCounts = new Map<string, number>();
	const categoryAmounts = new Map<string, number>();
	let totalAmount = 0;
	for (const receipt of records) {
		categoryCounts.set(
			receipt.categoryId,
			(categoryCounts.get(receipt.categoryId) ?? 0) + 1,
		);
		categoryAmounts.set(
			receipt.categoryId,
			(categoryAmounts.get(receipt.categoryId) ?? 0) +
				Math.max(0, receipt.totalAmount),
		);
		totalAmount += receipt.totalAmount;
	}
	const topCategoryIds = [...categoryCounts.entries()]
		.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
		.slice(0, 3)
		.map(([categoryId]) => categoryId);
	return {
		period: window.key,
		unlockedCount: categoryCounts.size,
		totalCells: 128,
		streakDays: longestStreak(records),
		topCategoryIds,
		recordCount: records.length,
		totalAmount,
		categoryCounts: Object.fromEntries(categoryCounts),
		categoryAmounts: Object.fromEntries(categoryAmounts),
	};
}

export function summarizeReports(
	receipts: Receipt[],
	period: ReportPeriod,
	anchor = new Date(),
	monthStartDay = 1,
): ReportComparison {
	const windows = windowFor(period, anchor, monthStartDay);
	const current = summarizeWindow(receipts, windows.current);
	const previous = summarizeWindow(receipts, windows.previous);
	return {
		period,
		current,
		previous,
		delta: {
			recordCount: (current.recordCount ?? 0) - (previous.recordCount ?? 0),
			unlockedCount: current.unlockedCount - previous.unlockedCount,
			totalAmount: (current.totalAmount ?? 0) - (previous.totalAmount ?? 0),
		},
	};
}

export function exportJson(
	receipts: Receipt[],
	settings: Record<string, string> = {},
) {
	return JSON.stringify(
		{ schemaVer: 1, exportedAt: new Date().toISOString(), receipts, settings },
		null,
		2,
	);
}

export function exportCsv(receipts: Receipt[]): string {
	const header = [
		"receiptId",
		"transactedAt",
		"storeDisplay",
		"categoryId",
		"totalAmount",
		"source",
	].join(",");
	const rows = receipts.map((receipt) =>
		[
			receipt.receiptId,
			receipt.transactedAt,
			receipt.storeDisplay,
			receipt.categoryId,
			String(receipt.totalAmount),
			receipt.source,
		]
			.map(csvEscape)
			.join(","),
	);
	return [header, ...rows].join("\n");
}

function csvEscape(value: string): string {
	return /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

const CARD = {
	paper: "#f2f4f3",
	ink: "#1e2328",
	muted: "#5b636b",
	teal: "#007c7a",
	tag: "#ffd23f",
};

function won(value: number): string {
	return `${Math.round(value).toLocaleString("ko-KR")}원`;
}

/** Thermal-receipt tear edge on the top and bottom of the card. */
function tearEdges(
	ctx: CanvasRenderingContext2D,
	width: number,
	height: number,
) {
	ctx.fillStyle = "#d9dedd";
	ctx.fillRect(0, 0, width, height);
	ctx.fillStyle = CARD.paper;
	ctx.beginPath();
	const tooth = 16;
	ctx.moveTo(0, tooth);
	for (let x = 0; x <= width; x += tooth * 2) {
		ctx.lineTo(x + tooth, 0);
		ctx.lineTo(x + tooth * 2, tooth);
	}
	ctx.lineTo(width, height - tooth);
	for (let x = width; x >= 0; x -= tooth * 2) {
		ctx.lineTo(x - tooth, height);
		ctx.lineTo(x - tooth * 2, height - tooth);
	}
	ctx.closePath();
	ctx.fill();
}

function footer(
	ctx: CanvasRenderingContext2D,
	width: number,
	height: number,
	address: string,
) {
	ctx.strokeStyle = CARD.muted;
	ctx.setLineDash([6, 8]);
	ctx.beginPath();
	ctx.moveTo(84, height - 118);
	ctx.lineTo(width - 84, height - 118);
	ctx.stroke();
	ctx.setLineDash([]);
	ctx.fillStyle = CARD.ink;
	ctx.font = "700 30px system-ui, sans-serif";
	ctx.fillText("소비도감", 84, height - 64);
	ctx.fillStyle = CARD.teal;
	ctx.font = "600 28px ui-monospace, Menlo, monospace";
	const textWidth = ctx.measureText(address).width;
	ctx.fillText(address, width - 84 - textWidth, height - 64);
}

export interface ShareCardOptions {
	/** Amounts stay hidden unless the user turns on "금액 보이기". */
	showAmount?: boolean;
	address?: string;
}

export function renderShareCard(
	summary: ReportSummary,
	width = 1200,
	height = 630,
	options: ShareCardOptions = {},
): HTMLCanvasElement | null {
	if (typeof document === "undefined") return null;
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) return canvas;
	tearEdges(ctx, width, height);
	ctx.fillStyle = CARD.muted;
	ctx.font = "500 30px system-ui, sans-serif";
	ctx.fillText(`${summary.period} 소비 기록`, 84, 104);
	ctx.fillStyle = CARD.ink;
	ctx.font = "800 120px ui-monospace, Menlo, monospace";
	const count = `${summary.recordCount ?? summary.unlockedCount}`;
	ctx.fillText(count, 84, 250);
	const countWidth = ctx.measureText(count).width;
	ctx.font = "600 40px system-ui, sans-serif";
	ctx.fillText("건을 남겼어요", 100 + countWidth, 246);
	ctx.fillStyle = CARD.tag;
	ctx.fillRect(84, 290, 520, 56);
	ctx.fillStyle = CARD.ink;
	ctx.font = "700 30px system-ui, sans-serif";
	ctx.fillText(
		`도감 ${summary.unlockedCount} / ${summary.totalCells}칸 · 연속 ${summary.streakDays}일`,
		104,
		328,
	);
	if (options.showAmount && summary.totalAmount !== undefined) {
		ctx.fillStyle = CARD.teal;
		ctx.font = "700 44px ui-monospace, Menlo, monospace";
		ctx.fillText(won(summary.totalAmount), 84, 420);
	}
	footer(ctx, width, height, options.address ?? "sobidogam.com/dogam");
	return canvas;
}

export interface UnlockCardInput {
	categoryName: string;
	slug: string;
	cellNumber: number;
	totalCells: number;
	amount?: number | undefined;
	illustration?: CanvasImageSource | null;
}

/** #45 해금 카드: the moment a dogam cell opens, ready for the share sheet. */
export function renderUnlockCard(
	input: UnlockCardInput,
	options: ShareCardOptions = {},
	width = 1200,
	height = 630,
): HTMLCanvasElement | null {
	if (typeof document === "undefined") return null;
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) return canvas;
	tearEdges(ctx, width, height);
	if (input.illustration) {
		try {
			ctx.drawImage(input.illustration, width - 404, 70, 320, 320);
		} catch {
			// Illustration is decorative; the card stays valid without it.
		}
	}
	ctx.fillStyle = CARD.teal;
	ctx.font = "700 30px system-ui, sans-serif";
	ctx.fillText("새 칸이 열렸어요", 84, 110);
	ctx.fillStyle = CARD.tag;
	ctx.beginPath();
	ctx.moveTo(84, 150);
	ctx.lineTo(84 + 560, 150);
	ctx.lineTo(84 + 600, 200);
	ctx.lineTo(84 + 560, 250);
	ctx.lineTo(84, 250);
	ctx.closePath();
	ctx.fill();
	ctx.fillStyle = CARD.paper;
	ctx.beginPath();
	ctx.arc(116, 200, 12, 0, Math.PI * 2);
	ctx.fill();
	ctx.fillStyle = CARD.ink;
	ctx.font = "800 64px system-ui, sans-serif";
	ctx.fillText(input.categoryName.slice(0, 10), 150, 224);
	ctx.font = "600 34px system-ui, sans-serif";
	ctx.fillText(`${input.totalCells}칸 중 ${input.cellNumber}번째 칸`, 84, 320);
	if (options.showAmount && input.amount !== undefined) {
		ctx.fillStyle = CARD.teal;
		ctx.font = "700 44px ui-monospace, Menlo, monospace";
		ctx.fillText(won(input.amount), 84, 400);
	}
	footer(
		ctx,
		width,
		height,
		options.address ?? `sobidogam.com/dogam/${input.slug}`,
	);
	return canvas;
}

export function canvasBlob(
	canvas: HTMLCanvasElement | null,
): Promise<Blob | null> {
	if (!canvas) return Promise.resolve(null);
	return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export async function shareCardBlob(
	summary: ReportSummary,
	options: ShareCardOptions = {},
): Promise<Blob | null> {
	return canvasBlob(renderShareCard(summary, 1200, 630, options));
}

export function buildAnnualReportHtml(summary: ReportSummary): string {
	return `<!doctype html><html lang="ko"><meta charset="utf-8"><title>${summary.period} 소비도감 결산</title><style>body{font-family:system-ui,sans-serif;background:#f2f4f3;color:#1e2328;padding:48px}h1{font-size:42px}strong{color:#007c7a;font-size:72px}</style><h1>소비도감 ${summary.period} 결산</h1><p><strong>${summary.unlockedCount}</strong>개의 표본을 모았어요.</p><p>연속 기록 ${summary.streakDays}일 · 도감 ${summary.totalCells}칸</p><p>나의 소비를 관찰하고, 다음 표본을 기다려요.</p></html>`;
}

function ascii(value: string): string {
	return value
		.normalize("NFKD")
		.replace(/[^\x20-\x7e]/g, " ")
		.replace(/[\\()]/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function pdfText(
	text: string,
	x: number,
	y: number,
	size: number,
	color = "0.11 0.10 0.09",
) {
	return `${color} rg BT /F1 ${size} Tf ${x} ${y} Td (${ascii(text)}) Tj ET`;
}

/**
 * Generates a deterministic, single-page PDF without a browser dependency.
 * The browser can offer print preview separately, while this function always
 * produces a real downloadable PDF file for offline export and verification.
 */
export function buildAnnualReportPdf(
	summary: ReportSummary,
	previous?: ReportSummary,
	archive?: MemoryArchiveSummary,
): Uint8Array {
	const recordCount = summary.recordCount ?? summary.unlockedCount;
	const totalAmount = summary.totalAmount ?? 0;
	const delta = previous
		? summary.unlockedCount - previous.unlockedCount
		: undefined;
	const stream = [
		"0.98 0.97 0.94 rg 0 0 595 842 re f",
		pdfText("SOBIDOGAM  /  ANNUAL FIELD REPORT", 54, 770, 12, "0.39 0.30 0.20"),
		pdfText(`${summary.period}  OBSERVATION`, 54, 716, 30),
		pdfText(
			"A quiet record of the places and moments you passed through.",
			54,
			685,
			12,
			"0.35 0.32 0.29",
		),
		"0.78 0.26 0.16 rg 54 560 487 1 re f",
		pdfText(String(summary.unlockedCount), 54, 590, 72, "0.78 0.26 0.16"),
		pdfText("opened cells", 190, 604, 16),
		pdfText(
			`${summary.totalCells} total cells`,
			190,
			580,
			12,
			"0.35 0.32 0.29",
		),
		pdfText(`${recordCount} records`, 54, 500, 18),
		pdfText(
			`${totalAmount.toLocaleString("en-US")} KRW observed`,
			54,
			472,
			12,
			"0.35 0.32 0.29",
		),
		pdfText(`${summary.streakDays} day longest streak`, 54, 425, 18),
		...(archive
			? [
					pdfText(`${archive.enrichedCount} remembered moments`, 54, 390, 16),
					pdfText(
						`${archive.favoriteCount} favorites / ${archive.revisitedPlaceCount} revisited places`,
						54,
						362,
						12,
						"0.35 0.32 0.29",
					),
					pdfText(
						`Most active: ${ascii(archive.mostActiveSeason ?? "-")} / ${ascii(archive.mostActiveWeekday ?? "-")}`,
						54,
						334,
						12,
						"0.35 0.32 0.29",
					),
				]
			: []),
		pdfText(
			delta === undefined
				? "Keep observing at your own pace."
				: `Compared with the previous period: ${delta >= 0 ? "+" : ""}${delta} cells`,
			54,
			archive ? 292 : 380,
			12,
			"0.35 0.32 0.29",
		),
		pdfText(
			"No rewards. Just your own collection.",
			54,
			90,
			11,
			"0.39 0.30 0.20",
		),
		pdfText("sobidogam.com", 445, 90, 10, "0.35 0.32 0.29"),
	].join("\n");
	const objects = [
		"<< /Type /Catalog /Pages 2 0 R >>",
		"<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
		"<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>",
		`<< /Length ${new TextEncoder().encode(stream).length} >>\nstream\n${stream}\nendstream`,
		"<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
	];
	let pdf = "%PDF-1.4\n%\xE2\xE3\xCF\xD3\n";
	const offsets = [0];
	for (let index = 0; index < objects.length; index += 1) {
		offsets.push(new TextEncoder().encode(pdf).length);
		pdf += `${index + 1} 0 obj\n${objects[index]}\nendobj\n`;
	}
	const xrefOffset = new TextEncoder().encode(pdf).length;
	pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
	for (let index = 1; index < offsets.length; index += 1)
		pdf += `${String(offsets[index]).padStart(10, "0")} 00000 n \n`;
	pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
	return new TextEncoder().encode(pdf);
}

export interface CategoryShare {
	categoryId: string;
	amount: number;
	ratio: number;
	previousAmount: number;
}

/**
 * Top categories by amount plus one "그 밖" row, with the previous window's
 * amount for the same category. Ratios are rounded to one decimal place.
 */
export function categoryShares(
	current: Record<string, number> | undefined,
	previous: Record<string, number> | undefined,
	limit = 6,
): CategoryShare[] {
	const entries = Object.entries(current ?? {}).filter(
		([, amount]) => amount > 0,
	);
	const total = entries.reduce((sum, [, amount]) => sum + amount, 0);
	if (!total) return [];
	const sorted = entries.sort(
		(a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
	);
	const head = sorted.slice(0, limit);
	const rest = sorted.slice(limit);
	const rows = head.map(([categoryId, amount]) => ({
		categoryId,
		amount,
		ratio: Math.round((amount / total) * 1000) / 10,
		previousAmount: previous?.[categoryId] ?? 0,
	}));
	if (rest.length) {
		const amount = rest.reduce((sum, [, value]) => sum + value, 0);
		rows.push({
			categoryId: "__rest",
			amount,
			ratio: Math.round((amount / total) * 1000) / 10,
			previousAmount: rest.reduce(
				(sum, [categoryId]) => sum + (previous?.[categoryId] ?? 0),
				0,
			),
		});
	}
	return rows;
}

export interface MonthDayCardInput {
	kind: "month_home" | "month_under";
	period: string;
	dayCount: number;
	totalAmount?: number;
}
/** Only already-sanitized aggregates enter the image; raw records never leave the device. */
export function renderMonthDayCard(
	input: MonthDayCardInput,
	options: ShareCardOptions = {},
): HTMLCanvasElement | null {
	if (typeof document === "undefined") return null;
	const canvas = document.createElement("canvas");
	canvas.width = 1080;
	canvas.height = 1350;
	const ctx = canvas.getContext("2d");
	if (!ctx) return null;
	tearEdges(ctx, 1080, 1350);
	ctx.fillStyle = CARD.muted;
	ctx.font = "500 32px system-ui, sans-serif";
	ctx.fillText(input.period, 80, 120);
	ctx.fillStyle = CARD.ink;
	ctx.font = "700 48px system-ui, sans-serif";
	ctx.fillText(
		input.kind === "month_home" ? "집에 있는 걸로" : "하루 몫 안에서",
		80,
		260,
	);
	ctx.fillText(input.kind === "month_home" ? "버틴 날" : "적게 쓴 날", 80, 330);
	ctx.strokeStyle = CARD.teal;
	ctx.lineWidth = 8;
	ctx.beginPath();
	ctx.arc(540, 650, 220, 0, Math.PI * 2);
	ctx.stroke();
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.arc(540, 650, 202, 0, Math.PI * 2);
	ctx.stroke();
	ctx.fillStyle = CARD.teal;
	ctx.textAlign = "center";
	ctx.font = "800 160px ui-monospace, Menlo, monospace";
	ctx.fillText(String(input.dayCount), 540, 680);
	ctx.font = "600 42px system-ui, sans-serif";
	ctx.fillText("일", 540, 755);
	ctx.textAlign = "left";
	ctx.fillStyle = CARD.ink;
	ctx.font = "500 30px system-ui, sans-serif";
	ctx.fillText("연속으로 찍지 않아도 쌓인 날은 그대로", 80, 1010);
	if (options.showAmount && input.totalAmount !== undefined) {
		ctx.font = "700 36px ui-monospace, Menlo, monospace";
		ctx.fillText(`이 기간 기록 ${won(input.totalAmount)}`, 80, 1090);
	}
	footer(ctx, 1080, 1350, options.address ?? "sobidogam.com");
	return canvas;
}
